import { z } from 'zod';

/**
 * Ef Group Classes response schemas
 */
export const efGroupClassSchema = z.object({
  classId: z.number(),
  isAvailable: z.boolean(),
  backgroundColor: z.string().nullable(),
  textColor: z.string().nullable(),
  backgroundColorLackOfPlaces: z.string().nullable(),
  textColorLackOfPlaces: z.string().nullable(),
  clubId: z.number(),
  clubName: z.string(),
  name: z.string(),
  photoUrl: z.string().url().nullable(),
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  duration: z.number(),
  instructorId: z.number(),
  instructorFullName: z.string(),
  instructorCalendarName: z.string().nullable(),
  instructorName: z.string().nullable(),
  instructorPhotoUrl: z.string().url().nullable(),
  participantsLimit: z.number(),
  isReplacement: z.boolean(),
  participantsNumber: z.number(),
  price: z.number(),
  currency: z.string().nullable(),
  calories: z.number(),
  roomName: z.string().nullable(),
  classReservationId: z.number().nullable(),
  isCourse: z.boolean(),
  classDefinitionId: z.number(),
  iconUrl: z.string().url().nullable(),
  virtualClassUrl: z.string().url().nullable(),
  waitingListOn: z.boolean(),
  waitingListLimit: z.number(),
  hasSubservices: z.boolean(),
  classDefinitionRating: z.number(),
});

export const efGroupClassesResponseSchema = z.object({
  results: z.array(efGroupClassSchema),
  totalCount: z.number(),
  next: z.string().url().nullable(),
  previous: z.string().url().nullable(),
});

export const efGetGroupClassesParamsSchema = z.object({
  DateFrom: z.string(),
  DateTo: z.string(),
  SearchText: z.string().optional(),
  RoomID: z.number().int().optional(),
  ClassDefinitionID: z.number().int().optional(),
  IsChainContext: z.boolean().optional(),
  InstructorID: z.number().int().optional(),
  Limit: z.number().int().optional(),
  Offset: z.number().int().optional(),
  SortBy: z.string().optional(),
});

export type EfGroupClass = z.infer<typeof efGroupClassSchema>;
export type EfGroupClassesResponse = z.infer<
  typeof efGroupClassesResponseSchema
>;
export type EfGetGroupClassesParams = z.infer<
  typeof efGetGroupClassesParamsSchema
>;

export const efIntGroupClassesResponseSchema = z.array(efGroupClassSchema);

/**
 * Ef Group Class Detail response schemas
 */
export const efCourseDaySchema = z.object({
  start: z.string(),
  duration: z.number(),
  dayOfWeek: z.number(),
});

export const efSubServiceSchema = z.object({
  serviceId: z.string(),
  serviceName: z.string(),
});

export const efGroupClassDetailResponseSchema = z.object({
  classId: z.number(),
  price: z.number(),
  currency: z.string().nullable(),
  isPaymentRequired: z.boolean(),
  calories: z.number(),
  clubID: z.number(),
  classDefinitionId: z.number(),
  classDefinitionName: z.string(),
  classDefinitionDescription: z.string(),
  classDefinitionPhotoUrl: z.string().url().nullable(),
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  duration: z.number(),
  instructorId: z.number(),
  instructorFullName: z.string(),
  instructorName: z.string().nullable(),
  instructorCalendarName: z.string().nullable(),
  instructorPhotoUrl: z.string().url().nullable(),
  instructorDescription: z.string().nullable(),
  instructorContactEmail: z.string().email().nullable(),
  instructorContactPhone: z.string().nullable(),
  instructorContactPhoneCode: z.string().nullable(),
  roomName: z.string().nullable(),
  participantsLimit: z.number(),
  isReplacement: z.boolean(),
  participantNumber: z.number(),
  classReservationId: z.number().nullable(),
  positionOnWaitList: z.number().nullable(),
  isCourse: z.boolean(),
  courseStartDate: z.string().datetime().nullable(),
  courseEndDate: z.string().datetime().nullable(),
  courseDays: z.array(efCourseDaySchema).nullable(),
  serviceId: z.number().nullable(),
  subServices: z.array(efSubServiceSchema).nullable(),
  canCancel: z.boolean(),
  canEnterPromoCode: z.boolean(),
  onlyReserveListAllowed: z.boolean(),
  iconUrl: z.string().url().nullable(),
  virtualClassUrl: z.string().url().nullable(),
  waitingListOn: z.boolean(),
  waitingListLimit: z.number(),
  deposit: z.number().nullable(),
  classDefinitionRating: z.number(),
  descriptionPhotoUrl: z.string().url().nullable(),
  descriptionVideoUrl: z.string().url().nullable(),
});

export type EfGroupClassDetail = z.infer<
  typeof efGroupClassDetailResponseSchema
>;

/**
 * Ef Group Class Reservation schemas
 */
export const efCreateClassReservationSchema = z.object({
  classId: z.number(),
  serviceId: z.number().optional(),
  promoCode: z.string().optional(),
});

export type EfCreateClassReservation = z.infer<
  typeof efCreateClassReservationSchema
>;

export const efCreateClassReservationResponseSchema = z.object({
  classReservations: z.array(
    z.object({
      classReservationId: z.number(),
      positionOnWaitList: z.number().nullable().optional(),
    }),
  ),
  isCourse: z.boolean(),
  price: z.number(),
});

export type EfCreateClassReservationResponse = z.infer<
  typeof efCreateClassReservationResponseSchema
>;

/**
 * Ef Get Member Class Reservations schemas
 */
// Query Parameters Schema
export const efGetMemberClassReservationsParamsSchema = z.object({
  DateFrom: z.string().optional(), // Optional based on docs
  DateTo: z.string().optional(), // Optional based on docs
  Limit: z.number().int().optional(),
  Offset: z.number().int().optional(),
});

export type EfGetMemberClassReservationsParams = z.infer<
  typeof efGetMemberClassReservationsParamsSchema
>;

// Single Reservation Item Schema (within the response)
export const efMemberClassReservationItemSchema = z.object({
  classReservationId: z.number(),
  className: z.string(),
  description: z.string(),
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  status: z.number(),
  positionOnWaitList: z.number(),
  clubId: z.number(),
  duration: z.number(),
  instructorId: z.number(),
  instructorName: z.string(),
  roomName: z.string(),
  classId: z.number(),
  canCancel: z.boolean(),
  cancellationBlockReasons: z.array(z.number()),
  iconUrl: z.string(),
  virtualClassUrl: z.string(),
  classReservationRating: z.number(),
});

export type EfMemberClassReservationItem = z.infer<
  typeof efMemberClassReservationItemSchema
>;

// Full Response Schema
export const efGetMemberClassReservationsResponseSchema = z.object({
  results: z.array(efMemberClassReservationItemSchema),
  totalCount: z.number(),
  next: z.string().url().nullable(),
  previous: z.string().url().nullable(),
});

export type EfGetMemberClassReservationsResponse = z.infer<
  typeof efGetMemberClassReservationsResponseSchema
>;
